"use client";

import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface SpotlightCardProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode;
	spotlightColor?: string;
}

export function SpotlightCard({
	children,
	className,
	spotlightColor = "rgba(76, 175, 80, 0.15)", // Default to primary green
	...props
}: SpotlightCardProps) {
	const divRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [opacity, setOpacity] = useState(0);

	const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!divRef.current) return;

		const div = divRef.current;
		const rect = div.getBoundingClientRect();

		setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
	};

	const handleFocus = () => {
		setOpacity(1);
	};

	const handleBlur = () => {
		setOpacity(0);
	};

	const handleMouseEnter = () => {
		setOpacity(1);
	};

	const handleMouseLeave = () => {
		setOpacity(0);
	};

	return (
		<div
			ref={divRef}
			onMouseMove={handleMouseMove}
			onFocus={handleFocus}
			onBlur={handleBlur}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			className={cn(
				"relative overflow-hidden rounded-3xl border border-border bg-card/30 backdrop-blur-xl transition-all duration-300",
				className,
			)}
			{...props}
		>
			{/* Noise Texture */}
			<div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none" />

			{/* Spotlight Effect */}
			<div
				className="pointer-events-none absolute -inset-px transition-opacity duration-300"
				style={{
					opacity,
					background: `radial-gradient(600px circle at ${position.x}px ${position.y}px, ${spotlightColor}, transparent 40%)`,
				}}
			/>

			{/* Content */}
			<div className="relative h-full">{children}</div>

			{/* Border Glow */}
			<div className="absolute inset-0 rounded-3xl ring-1 ring-white/10 pointer-events-none" />
		</div>
	);
}
